import React, { useState, useEffect } from "react";
import About from "./tabs/about/about";
import Certificate from "./tabs/certificate/certificate";
import Resume from "./tabs/resume/resume";
import Portofolio from "./tabs/portofolio/portofolio";
import BlogTab from "./tabs/blog/blog";
import { getProfile } from "../../api/profileAPI";
import { trackVisit } from "../../api/analyticsAPI";

const TAB_VIRTUAL_PATHS = {
  "1": "/tab/about",
  "2": "/tab/resume",
  "3": "/tab/certificate",
  "4": "/tab/portfolio",
  "5": "/tab/blog",
};

const TAB_MENU = [
  {
    id: "1",
    label: "About",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    id: "2",
    label: "Resume",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: "3",
    label: "Certificate",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  },
  {
    id: "4",
    label: "Portfolio",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: "5",
    label: "Blog",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    ),
  },
];

const Content = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Track initial tab view and tab switches as virtual paths
  useEffect(() => {
    trackVisit(TAB_VIRTUAL_PATHS["1"]);
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    trackVisit(TAB_VIRTUAL_PATHS[tabId]);
  };

  useEffect(() => {
    getProfile()
      .then((d) => {
        const lu = d.last_updated;
        if (lu) {
          setLastUpdated(
            new Date(lu).toLocaleDateString("id-ID", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          );
        }
      })
      .catch(() => {});
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case "1": return <About />;
      case "2": return <Resume />;
      case "3": return <Certificate />;
      case "4": return <Portofolio />;
      case "5": return <BlogTab />;
      default: return <About />;
    }
  };

  return (
    <div className="bg-[#111116] border border-zinc-800 rounded-2xl overflow-hidden min-h-screen">
      {/* Top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-cyan-400" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-800 gap-3">
        {/* Tabs */}
        <nav className="flex flex-wrap gap-1">
          {TAB_MENU.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-xl transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-900/30"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Last updated */}
        {lastUpdated && (
          <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-500">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            <span>Updated {lastUpdated}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div key={activeTab} className="animate-fade-in px-6 py-6">
        {renderContent()}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Content;
